import axios, { AxiosError } from "axios";
import { APP_URL } from "@/config/urls";
import { handleApiError } from "@/lib/utils";
import {
  serializeLeadDrop4IntegrationForApi,
  type LeadDrop4IntegrationPayload,
} from "@/lib/leadDrop4Integration";
import { getTenantHeadersAsync } from "@/lib/tenantKey";
import type {
  LeadDataProps,
  LeadProfileFields,
  ResendOTPProps,
} from "@/types/lead";
import { FRONTEND_API_ENDPOINTS } from "@/config/apiEndpoints/frontendUrls";
import { getEntryReferrer } from "@/utils/utils";
import { withMicrositeLsqPageUrls } from "@/lib/micrositeLsqContext";

const API_BASE_URL = APP_URL.API_BASE_URL;

/**
 * Does a lead already exist for this phone number? Pure read — no OTP, no SMS, no write.
 *
 * Replaces `getLeadByNumberAndEmail`, which did a read AND an SMS send behind one GET with
 * the number in the query string. Callers that also want an OTP use
 * `lookupLeadAndSendOtp` below, which is the same two steps made explicit.
 *
 * Returns the server envelope (`{ success, data: { leadExists, lead } }`) unchanged, so
 * every existing `res?.data?.lead` / `res?.data?.leadExists` read still works. `lead`
 * carries only the fields the forms actually use — see server/lib/leadLookupProjection.js.
 */
export const lookupLead = async (phone: string) => {
  try {
    const response = await axios.post(
      `${APP_URL.API_BASE_URL}${FRONTEND_API_ENDPOINTS.LEAD_LOOKUP}`,
      { mobile: String(phone || "").trim() },
      { withCredentials: true }
    );
    return response?.data;
  } catch (error) {
    console.error("lookupLead", error);
    return null;
  }
};

/**
 * Look the lead up and, when one exists, send it an OTP — what the old endpoint did in a
 * single call for every caller that did not pass `skipOtp`.
 *
 * The lead id from the lookup is handed to the OTP request ON PURPOSE. `leads` is not
 * unique on mobile, so a number can own several rows; without the id the OTP would be
 * stored against whichever row the OTP endpoint resolved on its own, and the caller's
 * later verify — which quotes the id it got HERE — would fail against a code filed
 * elsewhere. See the note in server/controllers/leads.js `sendOtpMobile`.
 *
 * No lead means no OTP, exactly as before: the old endpoint only sent one when the lookup
 * hit, and the caller creates the lead itself on the miss path. Returns null when the OTP
 * could not be sent, because the old single call failed as a whole in that case and every
 * caller's error path is written against that.
 */
export const lookupLeadAndSendOtp = async (phone: string) => {
  const lookup = await lookupLead(phone);
  const lead = lookup?.data?.lead;
  if (!lookup?.data?.leadExists || !lead?.id) return lookup;

  const otp = await sendOtpMobile(String(phone || "").trim(), String(lead.id));
  if (otp?.success === false) {
    console.error("lookupLeadAndSendOtp: OTP send failed", otp?.message);
    return null;
  }
  return lookup;
};

export type SubmitAfterOtpClientResult =
  | { success: true; data: Record<string, unknown>; _httpStatus?: undefined }
  | null;

/**
 * `leadId` targets an EXISTING row instead of letting the server resolve the mobile on its
 * own — required whenever the caller already looked the lead up, because a mobile can own
 * more than one row. Omitted, the server behaves exactly as it always did.
 */
export async function sendOtpMobile(mobile: string, leadId?: string) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}${FRONTEND_API_ENDPOINTS.SEND_OTP_MOBILE}`,
      {
        data: {
          mobile: String(mobile || "").trim(),
          ...(leadId ? { lead_id: String(leadId) } : {}),
        },
      },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error("sendOtpMobile", error);
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse
      ? { success: false, message: errorResponse.message }
      : { success: false, message: "Failed to send OTP" };
  }
}

export async function submitAfterOtp(
  lead_id: string,
  otp: string,
  isPreview: boolean,
  data?: Record<string, unknown>
): Promise<SubmitAfterOtpClientResult | Record<string, unknown>> {
  try {
    const body: Record<string, unknown> = { lead_id, otp, isPreview };
    if (data && Object.keys(data).length > 0) {
      body.data = data;
    }
    const response = await axios.post(
      `${API_BASE_URL}${FRONTEND_API_ENDPOINTS.SUBMIT}`,
      body,
      {
        withCredentials: true,
        validateStatus: (s) => s >= 200 && s < 300,
      }
    );
    return response.data;
  } catch (error) {
    console.error("submitAfterOtp", error);
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse
      ? { success: false, message: errorResponse.message }
      : { success: false, message: "Verification failed" };
  }
}

export async function verifyOtp(
  lead_id: string,
  otp: string,
  isPreview: boolean
) {
  try {
    const response = await axios.post(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.VERIFY_OTP}`,
      {
        lead_id,
        otp,
        isPreview,
      },
      {
        withCredentials: true,
      }
    );
    return [200].includes(response?.status) ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export type CreateLeadOptions = {
  /** Default true. Set false for lead drop variant 4 (no SMS OTP on create). */
  requireOtp?: boolean;
};

export async function createLead(
  data: LeadDataProps,
  isExistingLeadId: string,
  options?: CreateLeadOptions
) {
  const referrer = getEntryReferrer();
  const payload = {
    data: {
      mobile: data.phone,
      name: data.name,
      email: data.email,
      highest_qualification: data.qualification,
      area_of_interest: data.interest,
      source_media: data.media,
      source_campaign: data.campaign,
      source_refer_url: data.refURL,
      source_source: data.source,
      uni_code: data.uni_code,
      c1: data.c1,
      c2: data.c2,
      c3: data.c3,
      c4: data.c4,
      c5: data.c5,
      // Where the user originally came from (external referrer).
      // Sent as its own key — does NOT touch the ERP `c1` column.
      ...(referrer ? { lead_source_key: referrer } : {}),
    },
    lead_id: isExistingLeadId,
    require_otp: options?.requireOtp !== false,
  };

  try {
    const response = await axios.post(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.CREATE_LEAD}`,
      payload,
      {
        withCredentials: true,
      }
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateLeadProgramme(
  lead_id: string,
  data: Record<string, unknown>,
  isPreview: boolean
) {
  try {
    const response = await axios.post(
      `${API_BASE_URL}${FRONTEND_API_ENDPOINTS.UPDATE_LEAD_PROGRAMME}`,
      { lead_id, data, isPreview },
      {
        withCredentials: true,
        validateStatus: (s) => s >= 200 && s < 300,
      }
    );
    return response.data;
  } catch (error) {
    console.error("updateLeadProgramme", error);
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse
      ? { success: false, message: errorResponse.message }
      : { success: false, message: "Update failed" };
  }
}

// update lead
export async function updateLead(
  data: LeadDataProps,
  lead_id: string,
  isLeadFormExtraFields: string
) {
  let payload;

  // Where the user originally came from (external referrer).
  // Sent as its own key — does NOT touch the ERP `c1` column.
  const referrer = getEntryReferrer();

  if (isLeadFormExtraFields) {
    payload = {
      data: { ...data, ...(referrer ? { lead_source_key: referrer } : {}) },
      lead_id,
      source_refer_url: data.refURL,
    };
  } else
    payload = {
      data: {
        mobile: data.phone,
        name: data.name,
        email: data.email,
        highest_qualification: data.qualification || data.highest_qualification,
        area_of_interest: data.interest || data.course,
        isLeadExistInERP: data.isLeadExistInERP,
        source_source: data.source,
        uni_code: data.uni_code,
        source_refer_url: data.refURL,
        ...(referrer ? { lead_source_key: referrer } : {}),
      },
      lead_id,
    };

  try {
    const response = await axios.put(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.UPDATE_LEAD}`,
      payload,
      {
        withCredentials: true,
      }
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Write ONLY the attribution columns the current URL actually carries.
 *
 * A returning lead drops to the CRM off its STORED row, and no other call on the widget
 * re-enquiry path writes `source_media` / `source_campaign` (`updateLead` omits them), so a
 * re-enquiry from `?media=X&campaign=Y` used to go out with the first visit's values —
 * "Direct" whenever that visit had none. An absent parameter is not sent at all, so a URL
 * without them leaves the row exactly as it was.
 *
 * Never throws: attribution must not be the reason a lead fails to submit.
 */
export async function updateLeadUrlAttribution(lead_id: string) {
  if (!lead_id || typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const data: Record<string, string> = {};
  const media = params.get("media")?.trim();
  const campaign = params.get("campaign")?.trim();
  if (media) data.source_media = media;
  if (campaign) data.source_campaign = campaign;
  if (Object.keys(data).length === 0) return;
  try {
    await axios.put(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.UPDATE_LEAD}`,
      { data, lead_id },
      { withCredentials: true }
    );
  } catch (error) {
    console.error("updateLeadUrlAttribution", error);
  }
}

export const CAPTURE_LEAD_URL = `${API_BASE_URL}${FRONTEND_API_ENDPOINTS.CAPTURE_LEAD}`;

/**
 * Record-only non-verified capture / autosave. Ensures one lead row by mobile and
 * persists whatever partial fields are present (no OTP/SMS). Returns the leadId so
 * subsequent autosaves can target the same row. Additive — never changes form flow.
 */
export async function captureLead(
  data: Record<string, unknown>,
  leadId?: string,
  captureSessionId?: string,
  /**
   * The custom form this capture came from, or NULL when there is none (every widget).
   *
   * The ID only — never the destination itself. The server looks the drop destination up
   * from the row and pins it onto `lead_metadata`, so the non-verified sweep delivers an
   * ABANDONED lead to the same CRM a completed one would reach. Sending the destination
   * instead would let any caller choose which CRM this deployment delivers to.
   *
   * An explicit `null` is MEANINGFUL and is sent on the wire: it tells the server this
   * capture came through a widget, which delivers to the ERP, so any destination pinned by
   * a custom form the same person used earlier must be cleared. Leads converge by mobile,
   * so one row is touched by several forms, and "the form this lead most recently came
   * through" is the only rule that stays true. Omitting the key entirely still means "I am
   * an old build, change nothing" — which is what keeps a cached bundle from being affected
   * during a rollout.
   */
  customLeadFormId?: number | null
): Promise<string | null> {
  try {
    const body: Record<string, unknown> = { data };
    if (leadId) body.lead_id = leadId;
    // One form-fill session = one row. The server keys the row by this so editing the mobile/email
    // mid-fill updates the SAME row instead of forking a second lead.
    if (captureSessionId) body.capture_session_id = captureSessionId;
    // ALWAYS sent, null included — see the parameter doc. `null` is the widget's way of
    // saying "no custom form", which is what lets the server drop a stale destination pin.
    body.custom_lead_form_id = customLeadFormId ?? null;
    const response = await axios.post(CAPTURE_LEAD_URL, body, {
      withCredentials: true,
    });
    const id = response?.data?.data?.leadId;
    return id != null ? String(id) : null;
  } catch (error) {
    console.error("captureLead", error);
    return null;
  }
}

/**
 * Fire-and-forget capture on tab-close / abandon. Uses `text/plain` so the request
 * is CORS-simple (no preflight — the server accepts text/plain as JSON) and sendBeacon
 * still includes cookies.
 */
export function captureLeadBeacon(
  data: Record<string, unknown>,
  leadId?: string,
  captureSessionId?: string,
  /**
   * The custom form this capture came from, or NULL when there is none (every widget).
   *
   * The ID only — never the destination itself. The server looks the drop destination up
   * from the row and pins it onto `lead_metadata`, so the non-verified sweep delivers an
   * ABANDONED lead to the same CRM a completed one would reach. Sending the destination
   * instead would let any caller choose which CRM this deployment delivers to.
   *
   * An explicit `null` is MEANINGFUL and is sent on the wire: it tells the server this
   * capture came through a widget, which delivers to the ERP, so any destination pinned by
   * a custom form the same person used earlier must be cleared. Leads converge by mobile,
   * so one row is touched by several forms, and "the form this lead most recently came
   * through" is the only rule that stays true. Omitting the key entirely still means "I am
   * an old build, change nothing" — which is what keeps a cached bundle from being affected
   * during a rollout.
   */
  customLeadFormId?: number | null
): void {
  try {
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
      return;
    }
    const payload: Record<string, unknown> = { data };
    if (leadId) payload.lead_id = leadId;
    if (captureSessionId) payload.capture_session_id = captureSessionId;
    // The abandon path is the one the sweep actually acts on, so it must carry this too —
    // including the explicit null that unpins a stale destination.
    payload.custom_lead_form_id = customLeadFormId ?? null;
    const blob = new Blob([JSON.stringify(payload)], { type: "text/plain" });
    navigator.sendBeacon(CAPTURE_LEAD_URL, blob);
  } catch (error) {
    console.error("captureLeadBeacon", error);
  }
}

/**
 * Persist the post-submit "Thank you" profile fields (location, job title,
 * industry, experience) onto an existing lead. DB-only — the backend
 * `PUT /leads/update` writes these columns and does not push them to the ERP
 * (mirrors chandigarh-campus-portal). No `isLeadExistInERP` flag is sent so the
 * update is never short-circuited.
 */
export async function updateLeadProfileFields(
  data: LeadProfileFields,
  lead_id: string
) {
  try {
    const response = await axios.put(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.UPDATE_LEAD}`,
      { data, lead_id },
      {
        withCredentials: true,
      }
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

//resend otp
export async function resendOtp(data: ResendOTPProps) {
  try {
    const response = await axios.post(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.RESEND_OTP}`,
      data,
      {
        withCredentials: true,
      }
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// ── Lead drop variants 3 & 4 (net-new opt-in) ─────────────────────────────────

/**
 * Lead drop variant 4 (no OTP) — push a saved lead to CRM.
 * Standalone always routes to ERP server-side; LSQ is used only for multi-tenant
 * microsite hosts. Tenant headers are attached only in multi-tenant mode.
 */
export async function syncLeadDrop4(
  lead_id: string,
  isPreview: boolean,
  integration: LeadDrop4IntegrationPayload,
) {
  try {
    const response = await axios.post(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.SYNC_LEAD_DROP4}`,
      {
        lead_id,
        isPreview,
        // Microsites also carry the page facts LSQ reports as landing_page / platformUrl.
        integration: withMicrositeLsqPageUrls(serializeLeadDrop4IntegrationForApi(integration)),
      },
      {
        withCredentials: true,
        headers: await getTenantHeadersAsync(),
      },
    );
    return [200].includes(response?.status) ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/** True when direct-create API accepted the lead (defensive if `submitted` is omitted). */
export function isCreateLeadDirectSuccess(res: unknown): boolean {
  if (!res || typeof res !== "object") return false;
  const r = res as { success?: boolean; data?: { submitted?: boolean | string } };
  if (r.success !== true) return false;
  const sub = r.data?.submitted;
  if (sub === undefined || sub === null) return true;
  return sub === true || sub === "true";
}

/** Human-readable error from `createLeadDirectQuery` failure response or thrown client error. */
export function getCreateLeadDirectErrorMessage(res: unknown): string {
  if (!res || typeof res !== "object") {
    return "Something went wrong. Please try again.";
  }
  const r = res as { error?: { message?: string }; message?: string };
  const msg = r.error?.message || r.message;
  return typeof msg === "string" && msg.trim()
    ? msg.trim()
    : "Something went wrong. Please try again.";
}

/** Lead drop variant 3 — create in DB only (no ERP, no sheet sync). */
export async function createLeadDirectQuery(
  data: LeadDataProps,
  isPreview: boolean,
) {
  const payload = {
    data: {
      mobile: data.phone,
      name: data.name,
      email: data.email,
      highest_qualification: data.qualification,
      area_of_interest: data.interest,
      source_media: data.media,
      source_campaign: data.campaign,
      source_refer_url: data.refURL,
      source_source: data.source,
      uni_code: data.uni_code,
      c1: data.c1,
      c2: data.c2,
      c3: data.c3,
      c4: data.c4,
      c5: data.c5,
    },
    isPreview,
  };

  try {
    const response = await axios.post(
      `${APP_URL.API_BASE_URL + FRONTEND_API_ENDPOINTS.CREATE_LEAD_DIRECT}`,
      payload,
      {
        withCredentials: true,
        headers: await getTenantHeadersAsync(),
      },
    );
    return [200, 201].includes(response?.status) ? response?.data : null;
  } catch (error) {
    const parsed = handleApiError(error as AxiosError);
    if (parsed) {
      return {
        success: false as const,
        error: { message: parsed.message },
        status: parsed.status,
      };
    }
    return {
      success: false as const,
      error: { message: "Something went wrong. Please try again." },
    };
  }
}

/**
 * Provision a verified lead as an LMS learner (certification flow, after step 2).
 *
 * Sends ONLY the lead id — the server reads every field it forwards to the LMS
 * from the lead row, so a tampered payload cannot enrol arbitrary details.
 *
 * Never throws: any failure resolves to `false`, which lands the learner on the
 * "credentials will be emailed" card. A broken LMS must not break the form.
 */
export interface LmsSignupResult {
  granted: boolean;
  /**
   * One-time sign-in URL, when the LMS returns one. Held in memory for the
   * click that follows and never persisted — the token in the query string is a
   * live credential.
   */
  ssoLink?: string;
}

export async function lmsSignup(lead_id: string): Promise<LmsSignupResult> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}${FRONTEND_API_ENDPOINTS.LMS_SIGNUP}`,
      { lead_id },
      { withCredentials: true, validateStatus: (s) => s >= 200 && s < 300 }
    );
    const body = response.data?.data ?? response.data ?? {};
    return {
      granted: Boolean(body.granted),
      ...(typeof body.ssoLink === "string" && body.ssoLink ? { ssoLink: body.ssoLink } : {}),
    };
  } catch (error) {
    console.error("lmsSignup", error);
    return { granted: false };
  }
}
